import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import { CheckoutButton } from "@/components/site/CheckoutButton";
import { useSite, useImages } from "@/context/SiteContent";

const Shell = ({ children, testId }) => (
  <div className="pay-page" data-testid={testId}>
    <div className="pay-card">
      <Link to="/" className="pay-logo"><img alt="TransitionWorks" src="/tw-logo.png" /></Link>
      {children}
    </div>
  </div>
);

export function PaymentSuccess() {
  const { price } = useSite();
  const images = useImages();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", email: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "missing" }); return undefined; }
    let attempts = 0, timer;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await getPaymentStatus(sessionId);
        if (r.payment_status === "paid") return setState({ status: "paid", email: r.customer_email });
        if (["expired", "failed"].includes(r.payment_status)) return setState({ status: r.payment_status });
      } catch (e) { console.debug("Payment status poll failed, will retry", e?.message); }
      if (attempts < 8) timer = setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Shell testId="payment-success-page">
      {state.status === "checking" && <><span className="eyebrow">Confirming payment</span><h1>One moment...</h1><p className="lead">We are confirming your payment with Stripe. This usually takes a few seconds.</p><div className="pay-spinner" /></>}
      {state.status === "paid" && <>
        {images.cover && <img className="pay-cover" src={images.cover} alt="From Campus to Corporate cover" data-testid="payment-cover" />}
        <div className="booking-success-icon">✓</div>
        <span className="eyebrow">Payment confirmed</span>
        <h1>Welcome to the journey.</h1>
        <p className="lead">Thank you for getting <strong>From Campus to Corporate</strong>. Your purchase of {price} was successful{state.email ? <> and your interactive eBook is linked to <strong>{state.email}</strong></> : ""}.</p>
        <div className="pay-actions">
          <Link className="btn btn-gold" to="/read" data-testid="payment-read-btn">Read your eBook online</Link>
          <Link className="btn btn-light" to="/" data-testid="payment-home-btn">Back to Campus2Corp</Link>
        </div>
        <div className="pay-note" data-testid="payment-delivery-note"><strong>Create your reader account</strong> using this same email ({state.email || "your purchase email"}) to open the eBook. Your reflections, action items and notes save to your account and read on any device. For your protection it reads online only, so there is no file to lose or share.</div>
      </>}
      {["expired", "failed", "timeout", "missing"].includes(state.status) && <>
        <span className="eyebrow">Payment not confirmed</span>
        <h1>We could not confirm this payment yet.</h1>
        <p className="lead">{state.status === "timeout" ? "Stripe has not confirmed the payment yet. If you were charged, your access will still be honoured. You can refresh this page in a moment." : "This checkout session is no longer active. You can start a new checkout below."}</p>
        <div className="pay-actions"><CheckoutButton testId="payment-retry-btn">Try again • {price}</CheckoutButton><Link className="btn btn-light" to="/">Back to Campus2Corp</Link></div>
      </>}
    </Shell>
  );
}

export function PaymentCancel() {
  const { price } = useSite();
  return (
    <Shell testId="payment-cancel-page">
      <span className="eyebrow">Checkout cancelled</span>
      <h1>No payment was taken.</h1>
      <p className="lead">You closed the checkout before completing it. The eBook is still here whenever you are ready, and the 5Ps quiz and lesson explorer remain free.</p>
      <div className="pay-actions"><CheckoutButton testId="cancel-retry-btn">Get the eBook • {price}</CheckoutButton><Link className="btn btn-light" to="/" data-testid="cancel-home-btn">Back to Campus2Corp</Link></div>
    </Shell>
  );
}
